/**
 * Weight functions for Krippendorff's Alpha calculation.
 *
 * A weight function takes two category strings and returns a number between 0 and 1
 * indicating how similar they are. 1 = identical, 0 = completely different.
 *
 * Categories may be single codes ("A") or composites ("A|B") created when
 * a coder applies multiple codes to the same text range.
 *
 * Usage:
 *   import { jaccardWeight, identityWeight } from './WeightFunctions';
 *   calculatePA(excerpts, jaccardWeight);   // partial credit for overlapping codes
 *   calculatePA(excerpts, identityWeight);  // traditional exact-match only
 *
 * The default weight function used throughout the ICR calculation is jaccardWeight,
 * which gives partial credit when coders apply overlapping but not identical code sets.
 * For example, if one coder applies code A and another applies codes A and B (composite "A|B"),
 * identity weight treats this as complete disagreement (0), while Jaccard gives 0.5 credit
 * (1 shared code out of 2 total).
 */

/**
 * Identity weight: 1 if same category, 0 if different.
 * This is the traditional Krippendorff's Alpha behavior for nominal data.
 * No partial credit — codes either match exactly or they don't.
 *
 * @param {string} categoryA - First category (e.g., "A" or "A|B")
 * @param {string} categoryB - Second category
 * @returns {number} 1 if identical, 0 otherwise
 */
export function identityWeight(categoryA, categoryB) {
  return categoryA === categoryB ? 1 : 0;
}

/**
 * Jaccard weight: measures set overlap between two composite categories.
 * Parses "|"-separated code strings into sets, returns |intersection| / |union|.
 *
 * Examples:
 *   jaccardWeight("A", "A")     → 1     (identical)
 *   jaccardWeight("A", "B")     → 0     (no overlap)
 *   jaccardWeight("A", "A|B")   → 0.5   (1 shared / 2 total)
 *   jaccardWeight("A|B", "B|C") → 1/3   (1 shared / 3 total)
 *   jaccardWeight("A|B", "A|B") → 1     (identical sets)
 *
 * @param {string} categoryA - First category (e.g., "A" or "A|B")
 * @param {string} categoryB - Second category
 * @returns {number} Jaccard similarity between 0 and 1
 */
export function jaccardWeight(categoryA, categoryB) {
  const setA = new Set(categoryA.split('|'));
  const setB = new Set(categoryB.split('|'));
  let intersection = 0;
  for (const item of setA) {
    if (setB.has(item)) intersection++;
  }
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 1 : intersection / union;
}
